import { HashMap } from '@ngneat/transloco';
import { DELETE_INSTANCE_DEFAULT_NOTIFICATION, VERIFICATION_SERVER_UNREACHABLE_NOTIFICATION, YATTA_SUPPORT_EMAIL_ADDRESS } from '@platform-frontend/platform';
import { buildNotificationTranslationId, DateParams, PriceParams } from '@platform-frontend/transloco';
import { buildMailToLink, buildYattaExternalLink, TranslationId } from '@platform-frontend/util';
import { MARKETPLACE_TRANSLOCO_SCOPE } from './marketplace-transloco-scope';
import { SubscriptionIntervalParams } from './product/model/subscription-interval-params';

const buildTranslationId: (key: string, params?: HashMap) => TranslationId = (key: string, params: HashMap = {}) =>
  buildNotificationTranslationId(key, params, MARKETPLACE_TRANSLOCO_SCOPE);

// Shared messages
export const ABSTRACT_NOT_FOUND_NOTIFICATION = (instanceName: 'billingInformation' | 'paymentMethod'): TranslationId =>
  buildTranslationId('shared.abstractNotFound', { instanceName });
export const ABSTRACT_OUTDATED_NOTIFICATION = (instanceName: 'billingInformation' | 'paymentMethod'): TranslationId =>
  buildTranslationId('shared.abstractOutdated', { instanceName });
const SAVE_BILLING_ADDRESS_TRANSACTION_WITH_SOLUTION_ID_IN_USE_NOTIFICATION = buildTranslationId('shared.saveBillingAddressTransactionWithSolutionIdInUse');
export const WRONG_PRICING_INFORMATION_NOTIFICATION = buildTranslationId('shared.wrongPricingInformation');
export const INVALID_VAT_ID_NOTIFICATION = buildTranslationId('shared.invalidVatId');
export const VAT_ID_VERIFICATION_FAILED_NOTIFICATION = buildTranslationId('shared.vatIdVerificationFailed');
export const VAT_ID_NOT_MATCHING_COUNTRY_NOTIFICATION = buildTranslationId('shared.vatIdNotMatchingCountry');
const SAVE_INSTANCE_FAILED_NOTIFICATION = (instanceName: 'billingAddress' | 'creditCard' | 'bankAccount'): TranslationId =>
  buildTranslationId('shared.saveInstanceFailed', { instanceName });
const SPECIFIC_NOT_FOUND_NOTIFICATION = (instanceName: 'bankAccount' | 'creditCard'): TranslationId =>
  buildTranslationId('shared.specificNotFound', { instanceName });
const SPECIFIC_OUTDATED_NOTIFICATION = (instanceName: 'bankAccount' | 'creditCard'): TranslationId =>
  buildTranslationId('shared.specificOutdated', { instanceName });
const DELETE_SAVE_PAYMENT_METHOD_TRANSACTION_WITH_SOLUTION_ID_IN_USE_NOTIFICATION = (
  instanceName: 'paymentMethod' | 'creditCard' | 'bankAccount'
): TranslationId => buildTranslationId('shared.deleteSavePaymentMethodTransactionWithSolutionIdInUse', { instanceName });
export const AUTHORIZATION_REFUSED_NOTIFICATION = buildTranslationId('shared.authorizationRefused');
export const REDIRECT_ID_NOT_KNOWN_NOTIFICATION = buildTranslationId('shared.redirectIdNotKnown');

// Billing address
export const MSG_DELETE_BILLING_ADDRESS_TRANSACTION_WITH_SOLUTION_ID_IN_USE = SAVE_BILLING_ADDRESS_TRANSACTION_WITH_SOLUTION_ID_IN_USE_NOTIFICATION;
export const MSG_DELETE_BILLING_ADDRESS_OUTDATED_BILLING_INFORMATION_VERSION = ABSTRACT_OUTDATED_NOTIFICATION('billingInformation');
export const MSG_DELETE_BILLING_ADDRESS_NOT_FOUND = ABSTRACT_NOT_FOUND_NOTIFICATION('billingInformation');
export const MSG_DELETE_BILLING_ADDRESS_DEFAULT = DELETE_INSTANCE_DEFAULT_NOTIFICATION;
export const MSG_CREATE_UPDATE_BILLING_ADDRESS_TRANSACTION_WITH_SOLUTION_ID_IN_USE = SAVE_BILLING_ADDRESS_TRANSACTION_WITH_SOLUTION_ID_IN_USE_NOTIFICATION;
export const MSG_CREATE_UPDATE_BILLING_ADDRESS_OUTDATED_BILLING_INFORMATION_VERSION = ABSTRACT_OUTDATED_NOTIFICATION('billingInformation');
export const MSG_CREATE_UPDATE_BILLING_ADDRESS_WRONG_PRICING_INFORMATION = WRONG_PRICING_INFORMATION_NOTIFICATION;
export const MSG_CREATE_UPDATE_BILLING_ADDRESS_INVALID_VAT_ID = INVALID_VAT_ID_NOTIFICATION;
export const MSG_CREATE_UPDATE_BILLING_ADDRESS_VAT_SERVICE_NOT_REACHABLE = VAT_ID_VERIFICATION_FAILED_NOTIFICATION;
export const MSG_CREATE_UPDATE_BILLING_ADDRESS_COUNTRY_INVALID = VAT_ID_NOT_MATCHING_COUNTRY_NOTIFICATION;
export const MSG_CREATE_UPDATE_BILLING_ADDRESS_INTERNAL_VAT_CALCULATION_ERROR = VAT_ID_VERIFICATION_FAILED_NOTIFICATION;
export const MSG_CREATE_UPDATE_BILLING_ADDRESS_NOT_FOUND = ABSTRACT_NOT_FOUND_NOTIFICATION('billingInformation');
export const MSG_CREATE_UPDATE_BILLING_ADDRESS_DEFAULT = SAVE_INSTANCE_FAILED_NOTIFICATION('billingAddress');

// Payment method
export const MSG_CHECK_SCA_VERIFICATION_DEFAULT = VERIFICATION_SERVER_UNREACHABLE_NOTIFICATION;
export const MSG_CHECK_PAYMENT_METHOD_VERIFICATION_PAYPAL_DEFAULT = VERIFICATION_SERVER_UNREACHABLE_NOTIFICATION;
export const MSG_CHECK_PAYMENT_METHOD_VERIFICATION_PAYPAL_REDIRECT_ID_NOT_KNOWN = buildTranslationId(
  'checkPaymentMethodVerification.paypal.redirectIdNotKnown'
);
export const MSG_CREATE_UPDATE_CREDIT_CARD_DEFAULT = SAVE_INSTANCE_FAILED_NOTIFICATION('creditCard');
export const MSG_CREATE_UPDATE_CREDIT_CARD_AUTHORIZATION_REFUSED = AUTHORIZATION_REFUSED_NOTIFICATION;
export const MSG_CREATE_UPDATE_CREDIT_CARD_ENCRYPTED_DATA_EXPIRED = SAVE_INSTANCE_FAILED_NOTIFICATION('creditCard');
export const MSG_CREATE_UPDATE_CREDIT_CARD_ENCRYPTION_FAILED = buildTranslationId('createUpdateCreditCard.encryptionFailed.message', {
  supportMailLink: buildMailToLink(
    YATTA_SUPPORT_EMAIL_ADDRESS,
    buildTranslationId('createUpdateCreditCard.encryptionFailed.mailToYattaSupport.subject'),
    null,
    'b005270a-bfaf-46bf-a33b-960158921fac'
  )
});
export const MSG_CREATE_UPDATE_CREDIT_CARD_INVALID_ENCRYPTED_DATA = SAVE_INSTANCE_FAILED_NOTIFICATION('creditCard');
export const MSG_CREATE_UPDATE_CREDIT_CARD_NOT_FOUND = SPECIFIC_NOT_FOUND_NOTIFICATION('creditCard');
export const MSG_CREATE_UPDATE_CREDIT_CARD_OUTDATED_PAYMENT_INFORMATION_VERSION = SPECIFIC_OUTDATED_NOTIFICATION('creditCard');
export const MSG_CREATE_UPDATE_CREDIT_CARD_PAYMENT_METHOD_NOT_FOUND = SAVE_INSTANCE_FAILED_NOTIFICATION('creditCard');
export const MSG_CREATE_UPDATE_CREDIT_CARD_REDIRECT_ID_NOT_KNOWN = REDIRECT_ID_NOT_KNOWN_NOTIFICATION;
export const MSG_CREATE_UPDATE_CREDIT_CARD_REJECTED_PAYMENT = buildTranslationId('createUpdateCreditCard.rejectedPayment.message', {
  supportMailLink: buildMailToLink(
    YATTA_SUPPORT_EMAIL_ADDRESS,
    buildTranslationId('createUpdateCreditCard.rejectedPayment.mailToYattaSupport.subject'),
    null,
    '3d005b62-bd51-45a8-88e5-55e755f75ab3'
  )
});
export const MSG_CREATE_UPDATE_CREDIT_CARD_TRANSACTION_WITH_SOLUTION_ID_IN_USE =
  DELETE_SAVE_PAYMENT_METHOD_TRANSACTION_WITH_SOLUTION_ID_IN_USE_NOTIFICATION('creditCard');
export const MSG_CREATE_UPDATE_DIRECT_DEBIT_NOT_FOUND = SPECIFIC_NOT_FOUND_NOTIFICATION('bankAccount');
export const MSG_CREATE_UPDATE_DIRECT_DEBIT_DEFAULT = SAVE_INSTANCE_FAILED_NOTIFICATION('bankAccount');
export const MSG_CREATE_UPDATE_DIRECT_DEBIT_TRANSACTION_WITH_SOLUTION_ID_IN_USE =
  DELETE_SAVE_PAYMENT_METHOD_TRANSACTION_WITH_SOLUTION_ID_IN_USE_NOTIFICATION('bankAccount');
export const MSG_CREATE_UPDATE_DIRECT_DEBIT_OUTDATED_PAYMENT_INFORMATION_VERSION = SPECIFIC_OUTDATED_NOTIFICATION('bankAccount');
export const MSG_CREATE_UPDATE_PAYPAL_DEFAULT = buildTranslationId('createUpdatePaypal.default');
export const MSG_DELETE_PAYMENT_METHOD_TRANSACTION_WITH_SOLUTION_ID_IN_USE =
  DELETE_SAVE_PAYMENT_METHOD_TRANSACTION_WITH_SOLUTION_ID_IN_USE_NOTIFICATION('paymentMethod');
export const MSG_DELETE_PAYMENT_METHOD_OUTDATED_PAYMENT_INFORMATION_VERSION = ABSTRACT_OUTDATED_NOTIFICATION('paymentMethod');
export const MSG_DELETE_PAYMENT_METHOD_NOT_FOUND = ABSTRACT_NOT_FOUND_NOTIFICATION('paymentMethod');
export const MSG_DELETE_PAYMENT_METHOD_DEFAULT = DELETE_INSTANCE_DEFAULT_NOTIFICATION;

// Price updates
export const SINGLE_PRICE_UPDATED_NOTIFICATION = (
  type: 'increase' | 'decrease',
  product: string,
  oldPrice: PriceParams,
  newPrice: PriceParams,
  subscriptionInterval: SubscriptionIntervalParams
): TranslationId => buildTranslationId('priceUpdates.single', { type, product, oldPrice, newPrice, subscriptionInterval });
export const MSG_PRICE_UPDATES_SINGLE_DECREASE = (
  product: string,
  oldPrice: PriceParams,
  newPrice: PriceParams,
  period: SubscriptionIntervalParams
): TranslationId => SINGLE_PRICE_UPDATED_NOTIFICATION('decrease', product, oldPrice, newPrice, period);

// Subscription
export const MSG_CANCEL_SUBSCRIPTION_OUTDATED_CANCEL_PARAMS = buildTranslationId('cancelSubscription.outdatedCancelParams');
export const MSG_CANCEL_SUBSCRIPTION_DEFAULT = buildTranslationId('cancelSubscription.default.message', {
  supportMailLink: buildMailToLink(
    YATTA_SUPPORT_EMAIL_ADDRESS,
    buildTranslationId('cancelSubscription.default.mailToYattaSupport.subject'),
    null,
    'f636bf8a-b78d-4bfe-be48-b25d6fe82c80'
  )
});
export const MSG_CANCEL_SUBSCRIPTION_SUCCESS = buildTranslationId('cancelSubscription.success');
export const MSG_RESET_DEMO_SUCCESS = buildTranslationId('resetDemo.success');
export const MSG_NOTICE_PERIOD_BEFORE_NEXT_RENEWAL = (nextRenewalDate: Date): TranslationId => {
  const nextRenewalDateParams: DateParams = { date: nextRenewalDate, options: { dateStyle: 'medium' } };
  return buildTranslationId('cancelSubscription.noticePeriodBeforeNextRenewal', {
    nextRenewalDate: nextRenewalDateParams
  });
};

// Eula updates
export const MSG_TERMS_UPDATES_SINGLE = (product: string, termsPath: string): TranslationId =>
  buildTranslationId('termsUpdates.single', {
    product,
    termsLink: buildYattaExternalLink(termsPath, '_blank', 'link link-secondary link-underline', 'd869bfff-2307-4b47-9dc2-e4d3a1b9a320')
  });

// SaaS API key
export const MSG_REGENERATE_SAAS_API_KEY_DEFAULT = buildTranslationId('regenerateSaasApiKey.default.message', {
  supportMailLink: buildMailToLink(
    YATTA_SUPPORT_EMAIL_ADDRESS,
    buildTranslationId('regenerateSaasApiKey.default.mailToYattaSupport.subject'),
    null,
    '0f188862-7e24-41e9-a532-6e6af0624102'
  )
});
